import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FiRefreshCw, FiAlertCircle } from "react-icons/fi";
import { useFormikContext } from "formik";

import SolicitationEdit from "../../../../../../presentation/pages/admin/solicitation/edit";
import NfseNacionalCard from "../../../../../../presentation/components/nfse-nacional-card";

import {
  taxTypeList,
  especialTaxTypeList,
  planStatusList,
  addressTypeList,
} from "../../../../../../utils";

import {
  handleCepChange,
  getErrorMessage,
  makeCompany,
  showSuccessMessage,
  showErrorMessage,
  handleCnpjChange,
} from "../../../../../../presentation/helpers";
import { HelperCnpjInformaton } from "../../../../../../presentation/helpers/helper-cnpj-information";
import HelperMask from "../../../../../../presentation/helpers/helper-mask";
import toastUtils from "../../../../../../utils/ToastUtils";
import {
  ConfirmDialogStyled,
  SyncButton,
} from "../../../../../../presentation/pages/developer/profile/render-form/styles";
import { theme } from "../../../../../../presentation/styles/theme";

import {
  IPackage,
  ISolicitation,
} from "../../../../../../presentation/interfaces";
import { CompanyProps } from "../../../../../../domain/models/company.model";

import { makeRemotePackage } from "../../../../usecases/package/remote-package-factory";
import { makeRemoteSolicitation } from "../../../../usecases/solicitation/remote-solicitation-factory";
import { makeRemoteDeveloperPackage } from "../../../../usecases/developer-package/remote-developer-package-factory";
import { makeRemoteCompanies } from "../../../../usecases/companies/remote-companies-factory";
import { makeRemoteDeveloperCompanies } from "../../../../usecases/developer-companies/remote-developer-companies-factory";

const MakeSolicitationEdit: React.FC = () => {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = (): void => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const history = useHistory();

  const { id } = useParams<{ id: string }>();

  const [packages, setPackages] = useState<IPackage[]>([]);

  const [solicitation, setSolicitation] = useState<ISolicitation>();
  const [errorMessage, setErrorMessage] = useState<string | null>("");
  const [resyncing, setResyncing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCnpj, setPendingCnpj] = useState("");

  useEffect(() => {
    const fetchPackages = async (): Promise<void> => {
      const remotePackage = makeRemotePackage();
      try {
        const response = await remotePackage.loadAll();
        setPackages(response);
      } catch (error) {
        console.error("Error fetching packages:", error);
      }
    };

    fetchPackages();
  }, []);

  useEffect(() => {
    const fetchSolicitation = async (): Promise<void> => {
      const remoteSolicitation = makeRemoteSolicitation();
      try {
        const response = await remoteSolicitation.loadById(Number(id));
        setSolicitation(response);
      } catch (error) {
        console.error("Error fetching solicitation:", error);
      }
    };
    fetchSolicitation();
  }, [id]);

  const updatePlan = useCallback(async (values: any): Promise<void> => {
    const developerPackageRemote = makeRemoteDeveloperPackage();

    await developerPackageRemote.update(
      solicitation?.developerPackage?.id || 0,
      {
        status: values?.planStatus || "",
      },
    );
  }, [solicitation?.developerPackage?.id]);

  const makeCompanyToUpdate = useCallback((values: any): any => {
    const { address, ...rest } = makeCompany(values);

    return {
      id: solicitation?.developerCompany?.company?.id,
      address: {
        id: solicitation?.developerCompany?.company?.address?.id,
        ...address,
      },
      ...rest,
    };
  }, [solicitation?.developerCompany?.company]);

  const updateCompany = useCallback(async (values: any): Promise<void> => {
    await makeRemoteCompanies().update(
      solicitation?.developerCompany?.company?.id,
      makeCompanyToUpdate(values),
    );
  }, [solicitation?.developerCompany?.company, makeCompanyToUpdate]);

  const createCompany = useCallback(async (values: any): Promise<CompanyProps> =>
    makeRemoteCompanies().create(makeCompany(values)), []);

  const verifyDataToCreate = useCallback((values: any): boolean => {
    const requiredFields = [
      "companyCity",
      "companyNeighborhood",
      "companyNumber",
      "companyStreet",
      "companyState",
      "companyCep",
      "companyPhone",
      "especialTaxType",
      "fantasyName",
      "socialReason",
      "companyEmail",
      "municipalRegistration",
      "cnpj",
      "taxType",
    ];

    const isMissingFields = requiredFields.some(
      (field) =>
        values[field] === undefined ||
        values[field] === null ||
        values[field] === "",
    );

    return isMissingFields;
  }, []);

  const createDeveloperCompany = useCallback((companyId: number): Promise<any> =>
    makeRemoteDeveloperCompanies().create({
      company: { id: companyId },
      developer: { id: solicitation?.developer?.id },
      solicitation: { id: solicitation?.id as number },
    }), [solicitation?.developer?.id, solicitation?.id]);

  const updateSolicitationWithDeveloperCompany = useCallback((
    developerCompany: any,
  ): void => {
    setSolicitation(
      (prev) =>
      ({
        ...prev,
        developerCompany,
      } as ISolicitation),
    );
  }, []);

  const handleExistingCompanyUpdate = useCallback(async (data: any): Promise<void> => {
    await updateCompany(data);
  }, [updateCompany]);

  const handleNewCompanyRegistration = useCallback(async (data: any): Promise<void> => {
    const company = await createCompany(data);
    const developerCompany = await createDeveloperCompany(company.id as number);

    updateSolicitationWithDeveloperCompany(developerCompany);
  }, [createCompany, createDeveloperCompany, updateSolicitationWithDeveloperCompany]);

  const handleBasicRegister = useCallback(async (data: any): Promise<void> => {
    const isNewCompanyRegistration =
      !solicitation?.developerCompany && !verifyDataToCreate(data);
    const isExistingCompanyUpdate = !!solicitation?.developerCompany;

    if (isNewCompanyRegistration) {
      await handleNewCompanyRegistration(data);
    } else if (isExistingCompanyUpdate) {
      await handleExistingCompanyUpdate(data);
    }
  }, [solicitation?.developerCompany, verifyDataToCreate, handleNewCompanyRegistration, handleExistingCompanyUpdate]);

  const handleSubmissionError = useCallback((error: any): void => {
    const errorMsg = getErrorMessage(error);
    setErrorMessage(errorMsg);
    showErrorMessage(errorMsg);
  }, []);

  const handleSubmit = useCallback(async (data: any): Promise<void> => {
    try {
      await updatePlan(data);

      switch (solicitation?.type) {
        case "COMPANY_REGISTER":
          await handleExistingCompanyUpdate(data);
          break;

        case "BASIC_REGISTER":
          await handleBasicRegister(data);
          break;

        default:
          throw new Error("Tipo de solicitação desconhecido");
      }

      showSuccessMessage("Solicitação atualizada com sucesso!");

      history.push("/admin/solicitations");
    } catch (error: any) {
      handleSubmissionError(error);
    }
  }, [solicitation?.type, updatePlan, handleExistingCompanyUpdate, handleBasicRegister, history, handleSubmissionError]);

  const executeSync = useCallback(async (cnpj: string): Promise<void> => {
    try {
      setResyncing(true);
      const response = await HelperCnpjInformaton.getInformationCnpj(cnpj);
      if (!response) return;

      const newActivities = response.atividades?.map((item: any) => ({
        codigo: item.codigo,
        descricao: item.descricao,
        tipo: item.tipo,
      }));

      const updatedSolicitation = {
        ...solicitation,
        developerCompany: {
          ...solicitation?.developerCompany,
          company: {
            ...solicitation?.developerCompany?.company,
            socialReason: response.razao_social || solicitation?.developerCompany?.company?.socialReason || "",
            fantasyName: response.nome || solicitation?.developerCompany?.company?.fantasyName || "",
            email: response.email || solicitation?.developerCompany?.company?.email || "",
            subscriptionNumber: cnpj,
            cnaes: newActivities?.map((act: any) => ({
              code: act.codigo,
              description: act.descricao,
              codeFormatted: act.codigo,
              id: 0,
            })),
            address: {
              ...solicitation?.developerCompany?.company?.address,
              zipCode: response.endereco?.cep || solicitation?.developerCompany?.company?.address?.zipCode || "",
              place: response.endereco?.logradouro || solicitation?.developerCompany?.company?.address?.place || "",
              number: response.endereco?.numero || solicitation?.developerCompany?.company?.address?.number || "",
              district: response.endereco?.bairro || solicitation?.developerCompany?.company?.address?.district || "",
              complement: (response.endereco as any)?.complemento || solicitation?.developerCompany?.company?.address?.complement || "",
            },
          },
        },
      } as ISolicitation;

      setSolicitation(updatedSolicitation);

      toastUtils.showMessage({
        type: "success",
        message: "Informações sincronizadas com sucesso!",
      });
    } catch (error) {
      toastUtils.showMessage({
        type: "error",
        message: "Erro ao buscar informações do CNPJ.",
      });
    } finally {
      setResyncing(false);
    }
  }, [solicitation]);

  const SyncButtonComponent = useCallback(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { values } = useFormikContext<any>();
    return (
      <div style={{ alignSelf: "self-start" }}>
        <SyncButton
          type="button"
          disabled={resyncing}
          onClick={async () => {
            const formCnpj = HelperMask.removeCnpjMask(values?.cnpj || "");
            const currentCnpj = HelperMask.removeCnpjMask(solicitation?.developerCompany?.company?.subscriptionNumber || solicitation?.developer?.cpf || "");

            if (!HelperCnpjInformaton.isValidCnpj(formCnpj)) {
              toastUtils.showMessage({
                type: "warning",
                message: "CNPJ inválido ou não encontrado.",
              });
              return;
            }

            if (formCnpj !== currentCnpj && currentCnpj !== "") {
              setPendingCnpj(formCnpj);
              setShowConfirmModal(true);
            } else {
              await executeSync(formCnpj);
            }
          }}
          $resyncing={resyncing}
        >
          <FiRefreshCw />
          {resyncing ? "Buscando..." : "Sincronizar CNPJ"}
        </SyncButton>
      </div>
    );
  }, [resyncing, solicitation, executeSync]);

  const goBack = useCallback((): void => {
    history.goBack();
  }, [history]);

  const NfseNacionalComponent = useCallback(
    () => (
      <NfseNacionalCard
        mode="array"
        initialChecked={solicitation?.developerCompany?.company?.nfseNacional}
      />
    ),
    [solicitation?.developerCompany?.company?.nfseNacional],
  );

  const concatenatePhoneNumbers = useCallback((): string => {
    const ddd = solicitation?.developerCompany?.company?.phone?.ddd ?? "";
    const number = solicitation?.developerCompany?.company?.phone?.number ?? "";

    return ddd + number;
  }, [solicitation?.developerCompany?.company?.phone]);

  const concatenateMobilePhoneNumbers = useCallback((): string => {
    const ddd = solicitation?.developerCompany?.company?.cellPhone?.ddd ?? "";
    const number = solicitation?.developerCompany?.company?.cellPhone?.number ?? "";

    return ddd + number;
  }, [solicitation?.developerCompany?.company?.cellPhone]);

  const dataForm = useMemo(() => [
    {
      items: [
        {
          name: "title",
          component: "Title",
          title: "Dados CNPJ: ",
          number: 1,
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", alignItems: "end" },
          inputs: [
            {
              name: "socialReason",
              component: "Input",
              label: "Razão Social",
              title: "Razão Social",
              value:
                solicitation?.developerCompany?.company?.socialReason || "",
            },
            {
              component: "InputMask",
              mask: "CNPJ",
              name: "cnpj",
              label: "CNPJ",
              title: "CNPJ",
              value:
                solicitation?.developerCompany?.company?.subscriptionNumber ||
                "",
              onChange: (data: any, el: any) =>
                handleCnpjChange(data, el),
              validations: "cnpjValidationNotRequired",
            },
            {
              component: SyncButtonComponent,
            }
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            alignItems: "center",
          },
          inputs: [
            {
              name: "stateRegistration",
              component: "Input",
              label: "Inscrição Estadual",
              title: "Inscrição Estadual",
              value: solicitation?.developerCompany?.company?.stateRegistration,
            },
            {
              name: "municipalRegistration",
              component: "Input",
              label: "Inscrição Municipal",
              title: "Inscrição Municipal",
              value:
                solicitation?.developerCompany?.company?.municipalRegistration,
            },
            {
              name: "isMei",
              component: "InputCheck",
              title: "",
              label: "",
              checked: solicitation?.developerCompany?.company?.isMei || false,
              options: [
                {
                  value: "true",
                  label: "O CNPJ é MEI?",
                  name: "O CNPJ é MEI?",
                },
              ],
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "flex",
            width: "100%",
          },
          inputs: [
            {
              name: "companyActivities",
              component: "ActivitiesList",
              label: "Atividades Econômicas (CNAE)",
              title: "Atividades Econômicas (CNAE)",
              value: solicitation?.developerCompany?.company?.cnaes?.map((cnae: any) => ({
                codigo: cnae.code,
                descricao: cnae.description,
                tipo: cnae.id === 0 ? "principal" : "secundaria", // This is a hint for sync data
              })) || [],
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "2fr 1fr" },
          inputs: [
            {
              name: "fantasyName",
              component: "Input",
              label: "Nome Fantasia",
              title: "Nome Fantasia",
              value: solicitation?.developerCompany?.company?.fantasyName || "",
            },
            {
              component: "Input",
              name: "companyEmail",
              label: "E-mail Cadastrado",
              title: "E-mail Cadastrado",
              value: solicitation?.developerCompany?.company?.email || "",
              validations: "validEmail",
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "companyPhone",
              component: "InputMask",
              mask: "PHONE_NUMBER",
              label: "Telefone",
              title: "Telefone",
              placeholder: "(00) 0000-0000",
              value: concatenatePhoneNumbers(),
            },
            {
              name: "companyMobilePhone",
              component: "InputMask",
              mask: "MOBILE_NUMBER",
              label: "Celular",
              title: "Celular",
              placeholder: "(00) 00000-0000",
              value: concatenateMobilePhoneNumbers(),
            },
            {
              name: "taxType",
              component: "Select",
              label: "Regime Tributário",
              title: "Regime Tributário",
              value: solicitation?.developerCompany?.company?.taxType || "",
              options: taxTypeList.map((tax) => ({
                value: tax.ENUM,
                label: tax.label,
              })),
            },
            {
              name: "especialTaxType",
              component: "Select",
              label: "Regime Tributário Especial",
              title: "Regime Tributário Especial",
              value:
                solicitation?.developerCompany?.company?.especialTaxType || especialTaxTypeList.find((item) => item.value === 9)?.ENUM,
              options: especialTaxTypeList.map((tax) => ({
                value: tax.ENUM,
                label: tax.label,
              })),
            },
          ],
        },
        {
          component: "InputGroup",
          hasDivider: true,
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "simplesNacional",
              component: "InputCheck",
              title: "",
              label: "",
              checked:
                solicitation?.developerCompany?.company?.simpleNacional ||
                false,
              options: [
                {
                  value: "true",
                  label: "Optante pelo Simples Nacional",
                  name: "Optante pelo Simples Nacional",
                },
              ],
            },
            {
              name: "culturalIncentive",
              component: "InputCheck",
              title: "",
              label: "",
              checked:
                solicitation?.developerCompany?.company?.culturalIncentive ||
                false,
              options: [
                {
                  value: "true",
                  label: "Incentivo a Cultura",
                  name: "Incentivo a Cultura",
                },
              ],
            },
            {
              name: "taxIncentive",
              component: "InputCheck",
              title: "",
              label: "",
              checked:
                solicitation?.developerCompany?.company?.taxIncentive || false,
              options: [
                {
                  value: "true",
                  label: "Taxa de incentivo",
                  name: "Taxa de incentivo",
                },
              ],
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Nota Fiscal Nacional: ",
          number: 2,
        },
        {
          component: "InputGroup",
          hasDivider: true,
          style: { display: "grid", gridTemplateColumns: "1fr" },
          inputs: [
            {
              name: "nfseNacional",
              component: NfseNacionalComponent,
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Endereço do CNPJ: ",
          number: 3,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
          },
          inputs: [
            {
              name: "companyCep",
              component: "InputMask",
              mask: "CEP",
              label: "CEP",
              title: "CEP",
              value:
                solicitation?.developerCompany?.company?.address?.zipCode || "",
              onChange: (data: any, el: any) =>
                handleCepChange(data, el, "company"),
            },
            {
              name: "tipoLogradouro",
              component: "Select",
              label: "Tipo de Logradouro",
              title: "Tipo de Logradouro",
              value:
                solicitation?.developerCompany?.company?.address
                  ?.tipoLogradouro || "",
              required: true,
              validations: "inputRequired",
              options: addressTypeList.map((address) => ({
                value: address.ENUM,
                label: address.label,
              })),
            },
            {
              component: "Input",
              name: "companyStreet",
              label: "Rua",
              title: "Rua",
              value:
                solicitation?.developerCompany?.company?.address?.place || "",
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "companyNumber",
              component: "Input",
              label: "Número",
              title: "Número",
              value:
                solicitation?.developerCompany?.company?.address?.number || "",
            },
            {
              name: "companyNeighborhood",
              component: "Input",
              label: "Bairro",
              title: "Bairro",
              value:
                solicitation?.developerCompany?.company?.address?.district ||
                "",
            },
            {
              name: "companyComplement",
              component: "Input",
              label: "Complemento",
              title: "Complemento",
              value:
                solicitation?.developerCompany?.company?.address?.complement ||
                "",
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          hasDivider: true,
          inputs: [
            {
              name: "companyCity",
              component: "Input",
              label: "Cidade",
              title: "Cidade",
              value:
                solicitation?.developerCompany?.company?.address?.city || "",
            },
            {
              name: "companyState",
              component: "Input",
              label: "Estado",
              title: "Estado",
              value:
                solicitation?.developerCompany?.company?.address?.state || "",
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Plano: ",
          number: 4,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: isSmallScreen ? "1fr 1fr" : "1fr 1fr 1fr",
          },
          hasDivider: true,
          inputs: [
            {
              name: "plan",
              component: "Select",
              label: "Plano",
              title: "Plano",
              value: solicitation?.developerPackage.pack?.id || "",
              options: packages.map((pack) => ({
                value: pack.id,
                label: pack.packageEnum,
              })),
            },
            {
              name: "planStatus",
              component: "Select",
              label: "Status",
              title: "Status",
              value: solicitation?.developerPackage.status || "",
              options: planStatusList,
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "flex", gap: "16px", width: "100%", marginTop: 16 },
          inputs: [
            {
              name: "backButton",
              component: "Button",
              type: "button",
              title: "Voltar",
              onClick: goBack,
              color: false,
            },
            {
              name: "button",
              component: "Button",
              title: "Enviar",
              submittingText: "Carregando...",
              color: true,
            },
          ],
        },
      ],
    },
  ], [solicitation, packages, SyncButtonComponent, NfseNacionalComponent, goBack, isSmallScreen, concatenatePhoneNumbers, concatenateMobilePhoneNumbers]);

  const props = useMemo(() => ({
    dataForm,
    onSubmit: handleSubmit,
    errorMessage,
  }), [dataForm, handleSubmit, errorMessage]);

  return (
    <>
      <SolicitationEdit {...props} />
      <ConfirmDialogStyled
        visible={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        message="O CNPJ digitado é diferente do CNPJ anterior. Deseja realmente sincronizar os dados? Isso substituirá as informações atuais pelas do novo CNPJ."
        header="Confirmar sincronização de CNPJ"
        icon={() => <FiAlertCircle size={24} color={theme.colors.red} />}
        acceptLabel="Sim, sincronizar"
        rejectLabel="Não, cancelar"
        accept={() => {
          executeSync(pendingCnpj);
          setShowConfirmModal(false);
        }}
        reject={() => setShowConfirmModal(false)}
      />
    </>
  );
};

export default MakeSolicitationEdit;
